import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useId} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {addStyle, View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalDialog, ModalPanel} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingSmall, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import * as MobileApp from "@khan/mobile-app-integration";
import {mediaQueries} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

const StyledImg = addStyle("img");

interface Props {
    /**
     * Optional styles to be applied to the modal.
     */
    style?: StyleType;

    /**
     * Function to go to dismiss the modal.
     */
    onClose: () => void;

    /**
     * Function to go to the next step.
     */
    onNext: () => void;
}

/**
 * Renders an introduction to text-to-speech in a modal.
 *
 * This is a modal that displays an introduction to text-to-speech.  It is used
 * as part of text-to-speech onboarding.
 */
export const TextToSpeechIntroModal = (props: Props): React.ReactElement => {
    const {style, onClose, onNext} = props;
    // The ID used by Screen Readers to identify the dialog title.
    const dialogTitleId = useId();

    MobileApp.Guide.useUXStack("guide-text-to-speech-onboarding-intro", {
        onCancel: onClose,
        canConfirm: true,
        onConfirm: onNext,
        translatedTitle: t`Khanmigo can talk!`,
        translatedCancelLabel: t`Close`,
        translatedConfirmLabel: t`Select a voice`,
    });

    return (
        <ModalDialog style={style} aria-labelledby={dialogTitleId}>
            <ModalPanel
                closeButtonVisible
                content={
                    <View>
                        <StyledImg
                            alt=""
                            src={staticUrl(
                                "/images/guide/text-to-speech/intro.svg",
                            )}
                            style={styles.image}
                        />
                        <Strut size={spacing.large_24} />
                        <View style={styles.body}>
                            <HeadingSmall id={dialogTitleId}>
                                {t`Khanmigo can talk!`}
                            </HeadingSmall>
                            <Strut size={spacing.xSmall_8} />
                            <LabelMedium>
                                {t`Click the headphones to turn on Khanmigo Voice.`}
                            </LabelMedium>
                            <Strut size={spacing.medium_16} />
                            <LabelMedium>
                                {t`Click the play button to replay a message.`}
                            </LabelMedium>
                        </View>
                    </View>
                }
                footer={
                    <View style={styles.footer}>
                        <Button kind="secondary" onClick={onNext}>
                            {t`Select a voice`}
                        </Button>
                    </View>
                }
            />
        </ModalDialog>
    );
};

const styles = StyleSheet.create({
    body: {
        width: "unset",
        marginInlineStart: -spacing.xSmall_8,
        marginInlineEnd: -spacing.xSmall_8,
        marginBlockEnd: -spacing.medium_16,
        [mediaQueries.smOrSmaller]: {
            marginInlineStart: spacing.xSmall_8,
            marginInlineEnd: spacing.xSmall_8,
        },
    },
    footer: {
        paddingBlockEnd: spacing.medium_16,
        paddingInlineStart: spacing.xSmall_8,
        paddingInlineEnd: spacing.xSmall_8,
    },
    image: {
        flex: "1 1 auto",
        width: "unset",
        height: "auto",
        marginBlockStart: -spacing.xLarge_32,
        marginInlineStart: -spacing.xLarge_32,
        marginInlineEnd: -spacing.xLarge_32,

        [mediaQueries.smOrSmaller]: {
            marginBlockStart: -spacing.medium_16,
            marginInlineStart: -spacing.medium_16,
            marginInlineEnd: -spacing.medium_16,
        },
    },
});
